import React, { useState } from 'react';
import { Store, Smartphone, Package, TrendingUp, ShoppingBag, Sparkles, Zap, Users } from 'lucide-react';

const HowItWorks = () => {
  const [activeTab, setActiveTab] = useState('merchants');

  const merchantSteps = [
    {
      icon: Store,
      title: 'Get Online Presence',
      description: 'We provide the technology platform to bring your fashion store online instantly.'
    },
    {
      icon: Package,
      title: 'Integrate Logistics',
      description: 'Our logistics network connects directly to your store for seamless fulfillment.'
    },
    {
      icon: TrendingUp,
      title: 'Get Fashion Insights',
      description: 'Understand trends and customer preferences with real-time analytics.'
    },
    {
      icon: Zap,
      title: 'Grow Your Business',
      description: 'Reach more customers, deliver faster, and scale your fashion retail operations.'
    }
  ];

  const consumerSteps = [
    {
      icon: Smartphone,
      title: 'Browse Fashion Stores',
      description: 'Discover apparel, footwear, and accessories from nearby fashion stores.'
    },
    {
      icon: Sparkles,
      title: 'AI Style Recommendations',
      description: 'Get personalized style suggestions powered by smart algorithms.'
    },
    {
      icon: ShoppingBag,
      title: 'Try & Buy Fashion',
      description: 'Store-like experience with seamless refunds and easy returns.'
    },
    {
      icon: Zap,
      title: 'Lightning Fast Delivery',
      description: 'Get your fashion orders delivered in minutes from local stores.'
    }
  ];

  const steps = activeTab === 'merchants' ? merchantSteps : consumerSteps;

  return (
    <section id="how-it-works" className="py-24 bg-gradient-to-br from-gray-50 to-cream/30">
      <div className="container mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            How It <span className="text-coral">Works</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto mb-8">
            A seamless ecosystem connecting retailers and consumers through technology
          </p>

          {/* Tab Switcher */}
          <div className="inline-flex bg-white rounded-2xl p-2 shadow-lg border border-gray-100">
            <button
              onClick={() => setActiveTab('merchants')}
              className={`px-8 py-3 rounded-xl font-semibold transition-all ${
                activeTab === 'merchants'
                  ? 'bg-coral text-white shadow-md'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <Store className="w-5 h-5 inline-block mr-2 mb-1" />
              For Merchants
            </button>
            <button
              onClick={() => setActiveTab('consumers')}
              className={`px-8 py-3 rounded-xl font-semibold transition-all ${
                activeTab === 'consumers'
                  ? 'bg-coral text-white shadow-md'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <Users className="w-5 h-5 inline-block mr-2 mb-1" />
              For Consumers
            </button>
          </div>
        </div>

        {/* Steps Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {steps.map((step, index) => {
            const Icon = step.icon;
            return (
              <div
                key={index}
                className="feature-card group"
              >
                {/* Icon */}
                <div className="mb-6">
                  <div className="w-16 h-16 bg-coral rounded-xl flex items-center justify-center shadow-lg mx-auto">
                    <Icon className="w-8 h-8 text-white" />
                  </div>
                </div>

                {/* Step Number */}
                <div className="flex items-center gap-3 mb-4 justify-center">
                  <span className="text-4xl font-bold text-coral/20">{index + 1}</span>
                  <h3 className="text-xl font-bold text-gray-900">{step.title}</h3>
                </div>

                {/* Description */}
                <p className="text-gray-600 leading-relaxed text-center">{step.description}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;
